import { PageHero } from "../components/PageHero";
import { Section, SectionHead, Card } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { ProductShowcase } from "../components/ProductShowcase";
import { LabistaChat } from "../components/LabistaChat";
import { HazriCheckIn } from "../components/HazriCheckIn";
import { BrowserFrame } from "../components/BrowserFrame";
import { CTASection } from "../sections/CTASection";
import { PRODUCTS, type Product } from "../data/products";

/**
 * /products
 *
 * Scales by adding an object to PRODUCTS. The visual for each one is
 * chosen by slug and falls back to the product's first screenshot, so a
 * third product renders correctly the moment it is added — with a real
 * screenshot if it has one, and without a placeholder pretending to be a
 * screenshot if it does not.
 */

function visualFor(product: Product) {
  if (product.slug === "agent-labista") return <LabistaChat />;

  if (product.slug === "hazri-book") {
    return (
      <div className="space-y-4">
        <BrowserFrame
          src={product.shots![0].src}
          alt={product.shots![0].alt}
          label="Hazri Book · Company dashboard"
          priority
        />
        <div className="flex justify-center">
          <div className="origin-top scale-[0.85]">
            <HazriCheckIn />
          </div>
        </div>
      </div>
    );
  }

  if (product.shots?.length) {
    return (
      <BrowserFrame
        src={product.shots[0].src}
        alt={product.shots[0].alt}
        label={product.name}
      />
    );
  }

  return null;
}

const SELECTION_RULES = [
  {
    t: "The workflow already exists on paper or WhatsApp",
    b: "We are not trying to invent a behaviour. Both products replaced something people were already doing daily — a hazri register, a sales rep answering enquiries — which is why adoption was never the hard part.",
  },
  {
    t: "The record has to survive an argument",
    b: "Payroll disputes, RERA claims, channel-partner attribution. Where being able to prove what happened is worth money, software that produces evidence is worth more than software that produces reports.",
  },
  {
    t: "Nobody serves the market properly",
    b: "Gulf contracting, Indian real estate. Large workforces, real budgets, and tooling that has not moved in twenty years because the incumbents are selling to somebody else.",
  },
];

export function Products() {
  return (
    <>
      <PageHero
        eyebrow="Products"
        title={
          <>
            What we build{" "}
            <span className="text-gradient">for ourselves.</span>
          </>
        }
        lede="Hazri Book is in production in Saudi Arabia and we operate it ourselves — which is where most of what we know about building software for other people came from. Agent Labista is still in the lab."
        primary={{ href: "/contact", label: "Book a demo" }}
        secondary={{ href: "/work", label: "Read the case studies" }}
      />

      <Section id="portfolio" tight>
        <div className="container space-y-5">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.slug}>
              <ProductShowcase
                product={p}
                reverse={i % 2 === 1}
                secondaryHref="/contact"
                secondaryLabel="Book a demo"
                visual={visualFor(p)}
              />
            </Reveal>
          ))}
        </div>
      </Section>

      <Section id="selection">
        <div className="container">
          <SectionHead
            eyebrow="How we choose"
            title={
              <>
                Three tests a product idea{" "}
                <span className="text-gradient">has to pass.</span>
              </>
            }
            lede="We build few products deliberately. Operating software is the expensive part, and every product we add is a permanent commitment to the customers already on the last one."
          />

          <div className="mt-10 grid gap-4 lg:grid-cols-3">
            {SELECTION_RULES.map((r, i) => (
              <Reveal key={r.t} delay={i * 0.07}>
                <Card className="h-full p-6 sm:p-7">
                  <div className="font-mono text-[11px] tabular-nums text-white/25">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="mt-3 font-display text-base font-semibold leading-snug tracking-tight">
                    {r.t}
                  </h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-white/60">{r.b}</p>
                </Card>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <p className="mt-8 max-w-2xl text-sm leading-relaxed text-white/45">
              More are in the lab. We list a product here once customers are
              using it, not when we start building it.
            </p>
          </Reveal>
        </div>
      </Section>

      <CTASection
        title={
          <>
            Want one of these{" "}
            <span className="text-gradient">running for your business?</span>
          </>
        }
        lede="Both products are sold directly and set up by the people who built them. Tell us which one, and roughly how many sites or projects you are running, and we will show you it working on your own numbers."
        primary={{ href: "/contact", label: "Book a demo" }}
        secondary={{ href: "/software-development", label: "Or have us build yours" }}
      />
    </>
  );
}
